/**
 * Status **connection** sections for the Entity Details panel (ADR-024 §5 amended
 * 2026-07-24 and 2026-07-31; REQ-STATUS-027; Flow L §5).
 *
 * The Frame-3 connection detail — health rollup, throughput, errors, and
 * connection history — rendered through the entity-details :func:`SectionShell`
 * (no gear, mirroring the component detail + query-record) while reusing the
 * System Status row primitives.
 *
 * The incident-disable action this pane used to host retired with REQ-UI-164
 * (ADR-040 R-2). Its successor is a provider **stop**, which belongs on the
 * provider's own surface rather than on a connection's detail: an operator
 * stopping collection is acting on the source, not on one of the sockets it
 * happens to be using.
 *
 * # Two hosts, one field set
 *
 * Under the component → interface → connection hierarchy (architect ruling
 * 2026-07-31) a connection is drawn **nowhere on the canvas** and is normally
 * read as an **expanded drawer inside its host's roster** — the interface's
 * Selection Detail, or the ``Ingress`` component's while ingress interfaces
 * remain deferred (REQ-STATUS-044). Expanding a drawer is a disclosure, not a
 * selection, so no ``onBack`` is passed there: the host is still the single
 * inspected entity and there is nothing to step back **from**.
 *
 * The ``status-connection`` bus kind and its own full-panel rendering are
 * retained — the kind is part of a deployed v1.0.0 contract and nothing about it
 * is wrong; the hierarchy simply gave the roster a better home. When the
 * connection *is* the inspected entity the caller supplies ``onBack``, which
 * renders the breadcrumb that steps the selection up to the owning node
 * (Flow L §9).
 *
 * The connection itself is always resolved from the **live** per-component
 * connection list (:func:`useComponentConnections`) by whichever surface hosts
 * it — there is no per-connection BFF route (decision 4). A connection that has
 * aged out resolves to an honest empty state (CLAUDE.md §8.1), never a frozen
 * last-known row.
 *
 * Cross-references:
 *   - Flow L §5 (connection detail) / §9 (back); REQ-STATUS-021 (connections list).
 *   - ADR-024 §5 (composite identity, single focus); REQ-UI-046 / REQ-UI-059.
 */
import { CaretLeft } from "@phosphor-icons/react";
import type { StatusConnection } from "@/system-status/status-model";
import { TrendSeriesRow } from "@/system-status/trend-series-row";
import { useTrendExpansion } from "@/system-status/use-trend-expansion";
import {
  MetricRow,
  MiniStateRow,
  StatusSectionsProvider,
} from "@/system-status/status-detail-primitives";
import {
  formatBytes,
  formatStatusTimestamp as formatTimestamp,
} from "@/system-status/status-connection-format";
import { SectionShell } from "@/entity-details/section-shell";
import { useEntityDetailsSectionsContext } from "@/entity-details/entity-details-sections-context";
import {
  DEFAULT_HISTORY_WINDOW,
  HISTORY_WINDOW_LABELS,
  useConnectionHistory,
} from "@/system-status/use-component-history";

/** Props for :func:`StatusConnectionSections`. */
export interface StatusConnectionSectionsProps {
  /** The resolved connection (from the live per-component list). */
  connection: StatusConnection;
  /**
   * The component that owns this connection — the history read is scoped by it.
   *
   * Optional so an isolated render (a test, a storybook) still works; the trend
   * rows simply do not fetch without it, which is the honest degradation.
   */
  componentId?: string | null;
  /**
   * Human-readable name of the node the connection was drilled from. Only
   * meaningful alongside ``onBack``.
   */
  backLabel?: string;
  /**
   * Step the selection back up to the owning ``status-component`` (Flow L §9).
   *
   * Omitted when these sections render as an **expanded drawer** inside a
   * roster: the drawer never moved the selection, so there is nothing to step
   * back from and a breadcrumb there would offer to "leave" a place the
   * operator never left.
   */
  onBack?: () => void;
}

/**
 * Render the connection detail body: an optional back breadcrumb, then the
 * health / throughput / errors / history sections. Hosts the incident-disable
 * modal for an aggregator-scoped connection.
 */
/** Throughput series — the emitter's STORED per-second gauges (REQ-STATUS-071). */
const THROUGHPUT_KEYS: Record<string, string> = {
  bytes_per_sec: "Data rate",
  msgs_per_sec: "Message rate",
};

/** Error series — derived rates from the cumulative counters (REQ-STATUS-071). */
const ERROR_KEYS: Record<string, string> = {
  error_count: "Errors",
  dropped_count: "Dropped",
};

/**
 * The trend rows for one section of a connection drawer.
 *
 * Both sections call this with the same ``(componentId, connectionId, window)``,
 * so TanStack Query dedupes them to **one** request — REQ-STATUS-072 holds
 * without either section knowing about the other. Each is gated on its own
 * expansion, so a collapsed section still costs nothing.
 */
function ConnectionTrend({
  componentId,
  connectionId,
  sectionId,
  keys,
}: {
  componentId: string;
  connectionId: string;
  sectionId: string;
  keys: Record<string, string>;
}): React.ReactElement | null {
  const sections = useEntityDetailsSectionsContext();
  const expanded = sections === null ? true : sections.sections.isExpanded(sectionId);
  // REQ-STATUS-079 — the drawer gets the same affordance as the component and
  // interface panes, through the same hook and the same row. A second copy of
  // the toggle here is a second copy that drifts (CLAUDE.md §4.4).
  const expansion = useTrendExpansion();
  const history = useConnectionHistory({
    componentId,
    connectionId,
    window: DEFAULT_HISTORY_WINDOW,
    enabled: expanded,
  });
  const data = history.data;
  if (data === undefined) return null;
  const shown = data.series.filter((series) => series.key in keys);
  const note = Object.values(data.unavailable ?? {});
  if (shown.length === 0 && note.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1" data-testid={`${sectionId}-trend`}>
      {shown.map((series) => {
        const name = `${keys[series.key]}${series.aggregate === "rate" ? " per second" : ""}`;
        return (
          <TrendSeriesRow
            key={series.key}
            points={series.points}
            testId={`trend-${series.key}`}
            label={name}
            accessibleName={`${name}, ${HISTORY_WINDOW_LABELS[DEFAULT_HISTORY_WINDOW]}`}
            // A connection's metrics carry no catalog entry and no threshold —
            // the chart renders a plain magnitude axis, which is the intended
            // degradation (REQ-STATUS-054), not a gap to fill with a guess.
            window={DEFAULT_HISTORY_WINDOW}
            expanded={expansion.isExpanded(series.key)}
            onToggle={() => expansion.toggle(series.key)}
          />
        );
      })}
      {note.length > 0 && (
        <p className="text-meta italic text-text-muted" data-testid={`${sectionId}-trend-note`}>
          {/* A connection has NO aggregate, so a long window is an empty view
              rather than a thinner one — say so instead of looking quiet. */}
          History covers the last 14 days.
        </p>
      )}
    </div>
  );
}

export function StatusConnectionSections({
  connection,
  componentId = null,
  backLabel,
  onBack,
}: StatusConnectionSectionsProps): React.ReactElement {
  const connected = connection.conn_state === "connected";
  const showActionBar = onBack !== undefined || connection.aggregator_id != null;

  return (
    // Session-local collapse (REQ-UI-131). As a **drawer** these sections sit
    // inside the host roster's provider and inherit it, so a collapsed drawer
    // section survives the row being folded shut; as a full panel there is no
    // provider above and this one is the store (provide-if-absent).
    <StatusSectionsProvider>
      {showActionBar && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          {onBack !== undefined && (
            <button
              type="button"
              onClick={onBack}
              data-testid="connection-back"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              <CaretLeft size={12} weight="bold" aria-hidden="true" />
              Back to {backLabel ?? "component"}
            </button>
          )}
        </div>
      )}

      <SectionShell id="conn-health" title="Health rollup">
        <MiniStateRow
          testId="conn-rollup-connection"
          label="Connection"
          state={connected ? "healthy" : "unhealthy"}
          detail={connection.conn_state}
        />
        {connected && (
          <MiniStateRow
            testId="conn-rollup-activity"
            label="Activity"
            state={connection.activity === "active" ? "healthy" : "unknown"}
            detail={connection.activity}
          />
        )}
        {connection.valid !== null && connection.valid !== undefined && (
          <MiniStateRow
            testId="conn-rollup-validity"
            label="Validity"
            state={connection.valid ? "healthy" : "degraded"}
            detail={connection.valid ? "well-formed" : "validation failing"}
          />
        )}
      </SectionShell>

      <SectionShell id="conn-throughput" title="Throughput">
        <dl className="grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 text-body-sm">
          <MetricRow
            testId="conn-metric-data-rate"
            label="Data rate"
            value={`${formatBytes(connection.bytes_per_sec)}/s`}
          />
          <MetricRow
            testId="conn-metric-message-rate"
            label="Message rate"
            value={`${connection.msgs_per_sec.toFixed(0)}/s`}
          />
          <MetricRow
            testId="conn-metric-bytes-total"
            label="Cumulative bytes"
            value={formatBytes(connection.bytes_total)}
          />
          <MetricRow
            testId="conn-metric-messages-total"
            label="Cumulative messages"
            value={connection.msgs_total.toLocaleString()}
          />
        </dl>
        {/* Not mounted without an owning component: the history read is scoped
            by it, and an isolated render (a test, a storybook) has none. Gating
            here rather than inside keeps the hook out of a tree that has no
            session provider. */}
        {componentId !== null && (
          <ConnectionTrend
            componentId={componentId}
            connectionId={connection.connection_id}
            sectionId="conn-throughput"
            keys={THROUGHPUT_KEYS}
          />
        )}
      </SectionShell>

      <SectionShell id="conn-errors" title="Errors">
        <dl className="grid grid-cols-[1fr_max-content] gap-x-4 gap-y-1 text-body-sm">
          <MetricRow
            testId="conn-metric-errors"
            label="Errors"
            value={connection.error_count.toLocaleString()}
          />
          <MetricRow
            testId="conn-metric-dropped"
            label="Dropped / quarantined"
            value={connection.dropped_count.toLocaleString()}
          />
        </dl>
        {/* Not mounted without an owning component: the history read is scoped
            by it, and an isolated render (a test, a storybook) has none. Gating
            here rather than inside keeps the hook out of a tree that has no
            session provider. */}
        {componentId !== null && (
          <ConnectionTrend
            componentId={componentId}
            connectionId={connection.connection_id}
            sectionId="conn-errors"
            keys={ERROR_KEYS}
          />
        )}
      </SectionShell>

      <SectionShell id="conn-history" title="Connection history">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
          <MetricRow
            testId="conn-metric-connected-since"
            label="Connected since"
            value={formatTimestamp(connection.connected_since, "—")}
          />
          <MetricRow
            testId="conn-metric-last-connected"
            label="Last connected"
            value={formatTimestamp(connection.last_connected_at, "—")}
          />
          <MetricRow
            testId="conn-metric-last-message"
            label="Last message"
            value={formatTimestamp(connection.last_message_at, "never")}
          />
        </dl>
      </SectionShell>
    </StatusSectionsProvider>
  );
}
